import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useShopping } from '../../hooks/useShopping';
import { 
  ArrowLeft, 
  Plus, 
  Calendar, 
  DollarSign, 
  User,
  Edit,
  Trash2,
  List,
  MoreVertical,
  X,
  Save,
  ShoppingBag
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import AvatarOrInitial from '../Family/AvatarOrInitial';

export default function ShoppingLists() {
  const { family } = useFamilyContext();
  const { lists, loading, createList, updateList, deleteList } = useShopping();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'grocery',
    dueDate: '',
    budget: '',
    store: ''
  });

  const handleCreateList = async (e) => {
    e.preventDefault();
    try {
      await createList({
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null
      });
      
      setFormData({
        name: '',
        description: '',
        type: 'grocery',
        dueDate: '',
        budget: '',
        store: ''
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create list:', error);
    }
  };

  const handleUpdateList = async (e) => {
    e.preventDefault();
    if (!editingList) return;

    try {
      await updateList(editingList.id, {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null
      });
      
      setEditingList(null);
      setFormData({
        name: '',
        description: '',
        type: 'grocery',
        dueDate: '',
        budget: '',
        store: ''
      });
    } catch (error) {
      console.error('Failed to update list:', error);
    }
  };

  const handleDeleteList = async (listId) => {
    if (window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) {
      try {
        await deleteList(listId);
      } catch (error) {
        console.error('Failed to delete list:', error);
      }
    }
  };

  const startEdit = (list) => {
    setEditingList(list);
    setFormData({
      name: list.name,
      description: list.description || '',
      type: list.type,
      dueDate: list.due_date || '',
      budget: list.budget ? list.budget.toString() : '',
      store: list.store || ''
    });
  };

  const cancelEdit = () => {
    setEditingList(null);
    setFormData({
      name: '',
      description: '',
      type: 'grocery',
      dueDate: '',
      budget: '',
      store: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500 mx-auto mb-4"></div>
          <p className="text-stone-600 font-bold">Loading shopping lists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto p-5">
        {/* Header - Enhanced Neo-brutalist */}
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/shopping"
              className="p-3 rounded-xl hover:bg-stone-100 transition-all border-2 border-slate-900 bg-white min-h-[48px] min-w-[48px] flex items-center justify-center"
              style={{ boxShadow: '3px 3px 0px #0f172a' }}
            >
              <ArrowLeft className="w-5 h-5 text-stone-900" />
            </Link>
            <div className="flex-1">
              <h1 className="text-4xl font-black text-slate-900 mb-2">
                Shopping Lists 📝
              </h1>
              <p className="text-lg font-bold text-stone-700">
                Organize your family shopping like a pro!
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center gap-2 px-6 py-4 bg-coral-500 text-white font-black rounded-xl hover:bg-coral-600 transition-all border-3 border-slate-900 min-h-[56px]"
              style={{ 
                boxShadow: '4px 4px 0px #0f172a',
                transform: 'translate(0, 0)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translate(-2px, -2px)';
                e.target.style.boxShadow = '6px 6px 0px #0f172a';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translate(0, 0)';
                e.target.style.boxShadow = '4px 4px 0px #0f172a';
              }}
            >
              <Plus className="w-5 h-5" />
              New List
            </button>
          </div>
        </header>

        {/* Create/Edit Form - Enhanced */}
        {(showCreateForm || editingList) && (
          <div className="bg-white rounded-2xl p-8 border-4 border-slate-900 shadow-lg mb-8"
               style={{ boxShadow: '6px 6px 0px #0f172a' }}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-black text-slate-900">
                {editingList ? '✏️ Edit List' : '✨ Create New List'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  cancelEdit();
                }}
                className="p-2 hover:bg-stone-100 rounded-lg border-2 border-slate-900 bg-white transition-all"
                style={{ boxShadow: '2px 2px 0px #0f172a' }}
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>

            <form onSubmit={editingList ? handleUpdateList : handleCreateList} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-slate-900 mb-2">
                    List Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Weekly groceries, Party supplies, etc."
                    className="w-full px-4 py-3 border-3 border-slate-900 rounded-xl focus:outline-none focus:border-coral-500 transition-all font-semibold"
                    style={{ boxShadow: '3px 3px 0px #0f172a' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-slate-900 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-4 py-3 border-3 border-slate-900 rounded-xl focus:outline-none focus:border-coral-500 transition-all font-semibold"
                    style={{ boxShadow: '3px 3px 0px #0f172a' }}
                  >
                    <option value="grocery">🛒 Grocery</option>
                    <option value="household">🏠 Household</option>
                    <option value="special_event">🎉 Special Event</option>
                    <option value="back_to_school">📚 Back to School</option>
                    <option value="holiday">🎄 Holiday</option>
                    <option value="other">📋 Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-black text-slate-900 mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    className="w-full px-4 py-3 border-3 border-slate-900 rounded-xl focus:outline-none focus:border-coral-500 transition-all font-semibold"
                    style={{ boxShadow: '3px 3px 0px #0f172a' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-slate-900 mb-2">
                    Budget
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    placeholder="0.00"
                    className="w-full px-4 py-3 border-3 border-slate-900 rounded-xl focus:outline-none focus:border-coral-500 transition-all font-semibold"
                    style={{ boxShadow: '3px 3px 0px #0f172a' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-black text-slate-900 mb-2">
                    Store
                  </label>
                  <input
                    type="text"
                    value={formData.store}
                    onChange={(e) => setFormData({...formData, store: e.target.value})}
                    placeholder="Harris Teeter, Target, etc."
                    className="w-full px-4 py-3 border-3 border-slate-900 rounded-xl focus:outline-none focus:border-coral-500 transition-all font-semibold"
                    style={{ boxShadow: '3px 3px 0px #0f172a' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-slate-900 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Notes about this shopping list..."
                  className="w-full px-4 py-3 border-3 border-slate-900 rounded-xl focus:outline-none focus:border-coral-500 resize-y font-semibold"
                  style={{ boxShadow: '3px 3px 0px #0f172a' }}
                  rows={3}
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-coral-500 text-white py-3 px-6 rounded-xl font-black hover:bg-coral-600 flex items-center justify-center gap-2 border-3 border-slate-900 transition-all"
                  style={{ boxShadow: '4px 4px 0px #0f172a' }}
                >
                  <Save className="w-4 h-4" />
                  {editingList ? 'Update List' : 'Create List'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    cancelEdit();
                  }}
                  className="px-6 py-3 border-3 border-slate-900 rounded-xl font-black hover:bg-stone-50 transition-all"
                  style={{ boxShadow: '4px 4px 0px #0f172a' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lists - Enhanced Neo-brutalist Cards */}
        {lists.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border-4 border-slate-900 text-center"
               style={{ 
                 boxShadow: '6px 6px 0px #0f172a',
                 background: 'linear-gradient(135deg, #f8fafc, #ecfdf5)'
               }}>
            <div className="bg-coral-100 rounded-2xl p-6 mx-auto mb-6 w-24 h-24 flex items-center justify-center border-3 border-slate-900"
                 style={{ boxShadow: '4px 4px 0px #0f172a' }}>
              <ShoppingBag className="w-12 h-12 text-coral-700" />
            </div>
            <h3 className="text-3xl font-black text-slate-900 mb-3">No shopping lists yet! 📝</h3>
            <p className="text-lg font-bold text-stone-700 mb-8">
              Ready to get organized? Create your first shopping list and make grocery trips a breeze!
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-8 py-4 bg-coral-500 text-white font-black rounded-xl hover:bg-coral-600 transition-all border-3 border-slate-900"
              style={{ boxShadow: '4px 4px 0px #0f172a' }}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First List
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {lists.map(list => {
              const creator = [...(family?.parents || []), ...(family?.kids || [])]
                .find(member => member.id === list.created_by);
              
              const itemCount = list.items?.length || 0;
              const completedCount = list.items?.filter(item => 
                item.item?.status === 'purchased'
              ).length || 0;

              // Get emoji based on type
              const getTypeEmoji = (type) => {
                switch(type) {
                  case 'grocery': return '🛒';
                  case 'household': return '🏠';
                  case 'special_event': return '🎉';
                  case 'back_to_school': return '📚';
                  case 'holiday': return '🎄';
                  default: return '📋';
                }
              };

              return (
                <div key={list.id} className="bg-white rounded-2xl border-4 border-slate-900 overflow-hidden hover:transform hover:-translate-y-1 transition-all"
                     style={{ boxShadow: '4px 4px 0px #0f172a' }}>
                  {/* Header */}
                  <div className={`p-6 ${
                    list.type === 'grocery' ? 'bg-gradient-to-r from-coral-500 to-coral-600' :
                    list.type === 'household' ? 'bg-gradient-to-r from-azure-500 to-azure-600' :
                    list.type === 'special_event' ? 'bg-gradient-to-r from-coral-500 to-coral-600' :
                    'bg-gradient-to-r from-coral-500 to-coral-600'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getTypeEmoji(list.type)}</span>
                        <h3 className="text-xl font-black text-white truncate">
                          {list.name}
                        </h3>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => startEdit(list)}
                          className="p-2 hover:bg-white/20 rounded-lg text-white transition-all border-2 border-white/30"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center gap-4 text-white/90 text-sm font-bold">
                      <span className="capitalize bg-white/20 px-3 py-1 rounded-full">
                        {list.type.replace('_', ' ')}
                      </span>
                      <span className={`px-3 py-1 rounded-full font-black ${
                        list.status === 'active' ? 'bg-white/20' :
                        list.status === 'completed' ? 'bg-white/30' :
                        'bg-white/10'
                      }`}>
                        {list.status}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {list.description && (
                      <p className="text-stone-700 font-semibold mb-4 text-sm">{list.description}</p>
                    )}

                    {/* Stats - Enhanced */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-stone-50 rounded-xl border-2 border-slate-900"
                           style={{ boxShadow: '2px 2px 0px #0f172a' }}>
                        <div className="text-2xl font-black text-slate-900">{itemCount}</div>
                        <div className="text-sm font-bold text-stone-600">Items</div>
                      </div>
                      <div className="text-center p-4 bg-coral-50 rounded-xl border-2 border-slate-900"
                           style={{ boxShadow: '2px 2px 0px #0f172a' }}>
                        <div className="text-2xl font-black text-coral-800">{completedCount}</div>
                        <div className="text-sm font-bold text-stone-600">Done</div>
                      </div>
                    </div>

                    {/* Progress Bar - Enhanced */}
                    {itemCount > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center justify-between text-sm font-bold text-stone-700 mb-2">
                          <span>Progress</span>
                          <span>{Math.round((completedCount / itemCount) * 100)}%</span>
                        </div>
                        <div className="w-full bg-stone-200 rounded-full h-3 border-2 border-slate-900"
                             style={{ boxShadow: '2px 2px 0px #0f172a' }}>
                          <div 
                            className="bg-coral-500 h-full rounded-full transition-all duration-300"
                            style={{ width: `${(completedCount / itemCount) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Details */}
                    <div className="space-y-3 text-sm font-bold text-stone-700 mb-6">
                      {list.due_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>Due: {format(new Date(list.due_date), 'MMM d, yyyy')}</span>
                        </div>
                      )}
                      
                      {list.budget && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          <span>Budget: ${list.budget}</span>
                        </div>
                      )}

                      {creator && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>Created by {creator.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions - Enhanced */}
                    <div className="flex gap-3">
                      <Link
                        to={`/shopping?list=${list.id}`}
                        className="flex-1 bg-coral-500 text-white py-3 px-4 rounded-xl text-center font-black hover:bg-coral-600 transition-all no-underline border-2 border-slate-900"
                        style={{ boxShadow: '3px 3px 0px #0f172a' }}
                      >
                        View Items
                      </Link>
                      <button
                        onClick={() => startEdit(list)}
                        className="p-3 border-2 border-slate-900 rounded-xl hover:bg-stone-50 transition-all"
                        style={{ boxShadow: '3px 3px 0px #0f172a' }}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteList(list.id)}
                        className="p-3 border-2 border-coral-500 text-coral-600 rounded-xl hover:bg-coral-50 transition-all"
                        style={{ boxShadow: '3px 3px 0px #ef4444' }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}